import { _ as __rest } from './_tslib.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useRef } from 'react';
import { ALERT_ICONS, startFocus, generateClasses } from '@ids/tools';
import { Transition } from 'react-transition-group';
import { I as IdsIcon } from './icon.js';
import { I as IdsIconButton } from './icon-button.js';
import { u as useDefaultState } from './use-default-state.js';
import { s as styleInject } from './style-inject.es.js';

var css_248z = ".ids-animation-fade-in-start {\n  opacity: 0;\n}\n\n.ids-animation-fade-in-moderate-end {\n  opacity: 1;\n  -webkit-transition: opacity 200ms cubic-bezier(0.42, 0, 0.58, 1);\n  transition: opacity 200ms cubic-bezier(0.42, 0, 0.58, 1);\n}\n\n.ids-animation-fade-out-start {\n  opacity: 1;\n}\n\n.ids-animation-fade-out-moderate-output-end {\n  opacity: 0;\n  -webkit-transition: opacity 275ms cubic-bezier(0.42, 0, 0.58, 1);\n  transition: opacity 275ms cubic-bezier(0.42, 0, 0.58, 1);\n}\n\n.ids-animation-scale-x-down {\n  -webkit-transform: scaleX(var(--ids-scale-x-down, 0));\n          transform: scaleX(var(--ids-scale-x-down, 0));\n}\n\n.ids-animation-scale-x-up {\n  -webkit-transform: scaleX(var(--ids-scale-x-up, 100%));\n          transform: scaleX(var(--ids-scale-x-up, 100%));\n}\n\n.ids-animation-time-transform-slow {\n  -webkit-transition: -webkit-transform 2000ms cubic-bezier(0, 0, 0.58, 1);\n  transition: -webkit-transform 2000ms cubic-bezier(0, 0, 0.58, 1);\n  transition: transform 2000ms cubic-bezier(0, 0, 0.58, 1);\n  transition: transform 2000ms cubic-bezier(0, 0, 0.58, 1), -webkit-transform 2000ms cubic-bezier(0, 0, 0.58, 1);\n}\n\n.ids-animation-width-down {\n  width: var(--ids-width-down, 0);\n}\n\n.ids-animation-width-up {\n  width: var(--ids-width-up, 100%);\n}\n\n.ids-animation-time-width-slow {\n  /* stylelint-disable-next-line plugin/no-low-performance-animation-properties */\n  -webkit-transition: width 2000ms cubic-bezier(0, 0, 0.58, 1);\n  transition: width 2000ms cubic-bezier(0, 0, 0.58, 1);\n}\n\n/* stylelint-disable selector-pseudo-element-no-unknown */\n.ids-alert {\n  -webkit-box-align: start;\n      -ms-flex-align: start;\n          align-items: flex-start;\n  background-color: var(--ids_color_bg_base);\n  border: var(--ids_size_border_small) solid var(--ids_color_feedback_neutral);\n  border-radius: var(--ids_size_cornerRadius_card_02);\n  color: var(--ids_color_text_body_01);\n  display: -webkit-box;\n  display: -ms-flexbox;\n  display: flex;\n  font: var(--ids_textStyle_f02_s14_h20_wrg);\n  overflow: hidden;\n  -webkit-transition: opacity 200ms linear, -webkit-transform 200ms cubic-bezier(0.42, 0, 0.58, 1);\n  transition: opacity 200ms linear, -webkit-transform 200ms cubic-bezier(0.42, 0, 0.58, 1);\n  transition: transform 200ms cubic-bezier(0.42, 0, 0.58, 1), opacity 200ms linear;\n  transition: transform 200ms cubic-bezier(0.42, 0, 0.58, 1), opacity 200ms linear, -webkit-transform 200ms cubic-bezier(0.42, 0, 0.58, 1);\n  width: 100%;\n}\n.ids-alert__content {\n  padding: var(--ids_size_general_16x) 0 var(--ids_size_general_16x) var(--ids_size_general_16x);\n  text-align: left;\n  width: 100%;\n}\n.ids-alert__content > p {\n  font: var(--ids_textStyle_f02_s14_h20_wrg);\n}\n.ids-alert__icon {\n  -ms-flex-item-align: center;\n      align-self: center;\n  font-size: 0;\n  line-height: 1;\n  padding-left: var(--ids_size_general_16x);\n}\n.ids-alert .ids-icon-button {\n  border-radius: inherit;\n}\n.ids-alert ::ng-deep .ids-icon-button {\n  border-radius: inherit;\n}\n.ids-alert__title {\n  color: var(--ids_color_feedback_neutral);\n  font: var(--ids_textStyle_f02_s14_h20_wbd);\n}\n.ids-alert--success {\n  border-color: var(--ids_color_feedback_success);\n}\n.ids-alert--success .ids-alert__icon .ids-icon {\n  color: var(--ids_color_feedback_success);\n}\n.ids-alert--error {\n  border-color: var(--ids_color_feedback_error);\n}\n.ids-alert--error .ids-alert__icon .ids-icon {\n  color: var(--ids_color_feedback_error);\n}\n.ids-alert--closing {\n  opacity: 0;\n  -webkit-transform: scale(0.8);\n          transform: scale(0.8);\n}\n\n:host(ids-alert) {\n  display: block;\n}";
styleInject(css_248z);

/**
 * @category Component
 *
 * @example
 * ```ts
 * import { IdsAlert } from '@ids/react';
 * ```
 */
const IdsAlert = _a => {
  var {
      variant,
      closeLabel = 'fechar',
      className = '',
      show: externalShow,
      setShow: externalSetShow,
      icon,
      header,
      iconProps,
      transitionProps,
      children,
      origin
    } = _a,
    props = __rest(_a, ["variant", "closeLabel", "className", "show", "setShow", "icon", "header", "iconProps", "transitionProps", "children", "origin"]);
  const [show, setShow] = useDefaultState(Boolean(externalShow), externalSetShow);
  const iconName = variant && ALERT_ICONS[variant];
  const ref = useRef();
  const handleClose = () => {
    setShow(false);
  };
  const focusOrigin = () => {
    origin === null || origin === void 0 ? void 0 : origin.focus();
  };
  const iconTemplate = jsx("div", Object.assign({
    className: "ids-alert__icon"
  }, {
    children: jsx(IdsIcon, Object.assign({
      size: "L"
    }, iconProps, {
      children: iconName || 'informacao_outline'
    }))
  }));
  const render = state => {
    const classes = generateClasses({
      'ids-alert': true,
      'ids-alert--success': variant === 'success',
      'ids-alert--error': variant === 'error',
      'ids-alert--closing': state === 'exiting' || state === 'exited',
      [className]: className
    });
    return jsxs("div", Object.assign({
      className: classes
    }, props, {
      children: [icon && iconTemplate, jsxs("div", Object.assign({
        className: "ids-alert__content",
        ref: ref
      }, {
        children: [header && jsx("h2", Object.assign({
          className: "ids-alert__title",
          "data-start-focus": "1"
        }, {
          children: header
        })), jsx("p", Object.assign({
          "data-start-focus": "2"
        }, {
          children: children
        }))]
      })), jsx(IdsIconButton, Object.assign({
        "aria-label": closeLabel,
        onClick: handleClose,
        variant: "neutral",
        size: "S"
      }, {
        children: "fechar"
      }))]
    }));
  };
  return jsx(Transition, Object.assign({
    in: show,
    timeout: {
      enter: 200,
      exit: 200
    }
  }, transitionProps, {
    mountOnEnter: true,
    unmountOnExit: true,
    onExit: focusOrigin,
    onEnter: () => startFocus(ref.current)
  }, {
    children: state => render(state)
  }));
};

export { IdsAlert as I };
//# sourceMappingURL=alert.js.map
